import React, { useCallback, useEffect, useState } from 'react';

import Modal from '../common/Modal';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';
import { validateRequiredFields, deepTrim } from '../../utils/formValidation';

// Must match com.primedha.commsysplatform.whatsapp.constants.UserRoleType and the
// user_profile_user_role_check constraint.
const USER_ROLES = ['SYSTEM_ADMIN', 'ADMIN', 'USER'];

const emptyProfile = {
  userId: '',
  tenantId: '',
  userRole: '',
  email: '',
  firstName: '',
  lastName: '',
  address: '',
  orgId: '',
  empId: '',
  status: false,
  wabaIds: [],
};

const WaUserProfilePage = () => {

  const waApi = useWaApi();
  // SYSTEM_ADMIN and ADMIN may manage profiles; USER gets a read-only view.
  const { canManage, assignableRoles, assignableWabaIds } = useUserProfileAccess();
  const [profiles, setProfiles] = useState([]);
  const [wabaOptions, setWabaOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // Null when creating, the profile id when editing - the same modal serves both.
  const [editingId, setEditingId] = useState(null);
  const [profileForm, setProfileForm] = useState(emptyProfile);

  const initialFilters = {
    id: null,
    userId: null,
    userRole: null,
    email: null,
    firstName: null,
    orgId: null,
    empId: null,
    status: null,
    wabaId: null,
  };
  const [filters, setFilters] = useState(initialFilters);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [formError, setFormError] = useState({});
  const [actionErrors, setActionErrors] = useState({});
  const [createError, setCreateError] = useState(null);
  const [createMessage, setCreateMessage] = useState(null);

  const clearMessages = () => {
    setCreateError(null);
    setCreateMessage(null);
    setActionErrors({});
    setFormError({});
  };

  const resetFilters = () => {
    clearMessages();
    setFilters(initialFilters);
  };

  const fetchProfiles = useCallback(async (activeFilters) => {
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(activeFilters).map(([key, value]) => [key, value === '' ? null : value])
      );
      const response = await waApi.get(
        process.env.REACT_APP_GET_ALL_WA_USER_PROFILES_BY_PAGE_ENDPOINT,
        { params: { ...cleanedFilters, page, size } }
      );

      const { message, object, httpStatus } = response.data;
      if (httpStatus === 200) {
        setProfiles(object.content);
        setTotalPages(object.totalPages);
      } else {
        setCreateError(`Error: ${message}`);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching user profiles:', err);
      setError(err.message || 'An error occurred.');
      setLoading(false);
    }
  }, [waApi, page, size]);

  useEffect(() => {
    fetchProfiles(filters);
    // filters are applied explicitly via the Apply Filters button, not on every keystroke
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProfiles]);

  // WABA options for the many-to-many selector.
  useEffect(() => {
    const fetchWabas = async () => {
      try {
        const response = await waApi.get(process.env.REACT_APP_GET_ALL_WABA_DETAILS_ENDPOINT);
        if (response.data.httpStatus === 200) {
          setWabaOptions(response.data.object || []);
        }
      } catch (err) {
        console.error('Error fetching WABA details:', err);
      }
    };
    fetchWabas();
  }, [waApi]);

  useEffect(() => {
    if (modalOpen) {
      setFormError({});
    }
  }, [modalOpen]);

  // Only the roles the signed-in user may assign. A profile whose role is outside that set is
  // not editable at all, so there is nothing else to offer here.
  const selectableRoles = USER_ROLES.filter((role) => assignableRoles.includes(role));

  // Only the WABAs on the signed-in user's own profile can be handed to someone else, so the
  // picker offers exactly those. The service enforces the same rule.
  const selectableWabas = wabaOptions.filter((waba) => assignableWabaIds.includes(waba.wabaId));

  const handleInputChange = (e) => {
    const { name, type, checked, value } = e.target;
    setProfileForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // The API takes a boolean, so map the three-way dropdown back to true/false/null.
  const handleStatusFilterChange = (e) => {
    const value = e.target.value;
    const statusValue = value === 'Active' ? true : value === 'Inactive' ? false : null;
    setFilters((prev) => ({ ...prev, status: statusValue }));
  };

  const handleWabaSelectionChange = (e) => {
    const selected = Array.from(e.target.selectedOptions, (option) => option.value);
    setProfileForm((prev) => ({ ...prev, wabaIds: selected }));
  };

  const handleFilterInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const openCreateModal = () => {
    clearMessages();
    setEditingId(null);
    setProfileForm(emptyProfile);
    setModalOpen(true);
  };

  const openEditModal = (profile) => {
    clearMessages();
    setEditingId(profile.id);
    setProfileForm({
      userId: profile.userId ?? '',
      tenantId: profile.tenantId ?? '',
      userRole: profile.userRole ?? '',
      email: profile.email ?? '',
      firstName: profile.firstName ?? '',
      lastName: profile.lastName ?? '',
      address: profile.address ?? '',
      orgId: profile.orgId ?? '',
      empId: profile.empId ?? '',
      status: profile.status ?? false,
      wabaIds: profile.wabaIds ?? [],
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearMessages();

    const validation = validateRequiredFields({
      userId: { value: profileForm.userId, label: 'User Id' },
      userRole: { value: profileForm.userRole, label: 'User Role' },
      email: { value: profileForm.email, label: 'Email' },
      firstName: { value: profileForm.firstName, label: 'First Name' },
    });
    if (Object.keys(validation.errors).length > 0) {
      setFormError(validation.errors);
      return;
    }

    try {
      // wabaIds is a list; deepTrim only touches string fields.
      const payload = deepTrim({ ...profileForm });
      const response = editingId
        ? await waApi.put(`${process.env.REACT_APP_PUT_WA_USER_PROFILE_ENDPOINT}/${editingId}`, payload)
        : await waApi.post(process.env.REACT_APP_SET_WA_USER_PROFILE_ENDPOINT, payload);

      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
        setModalOpen(false);
        setProfileForm(emptyProfile);
        setEditingId(null);
        fetchProfiles(filters);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.response?.data?.message || err.message || 'An error occurred while saving the user profile.');
    }
  };

  const toggleActivationStatus = async (id, currentStatus) => {
    clearMessages();
    try {
      const newStatus = !currentStatus;
      const response = await waApi.patch(
        `${process.env.REACT_APP_SET_WA_USER_PROFILE_STATUS_ENDPOINT}/${id}`,
        { status: newStatus }
      );
      setProfiles((prev) =>
        prev.map((profile) => profile.id === id ? { ...profile, status: newStatus } : profile)
      );
      setCreateMessage(response.data.message);
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while updating the status.' }));
    }
  };

  const handleDelete = async (id) => {
    clearMessages();
    try {
      const response = await waApi.delete(`${process.env.REACT_APP_DELETE_WA_USER_PROFILE_ENDPOINT}/${id}`);
      if (response.data.httpStatus === 200) {
        // Soft delete server-side: the row stays and comes back as Inactive.
        setProfiles((prev) => prev.map((profile) => profile.id === id ? { ...profile, status: false } : profile));
        setCreateMessage(response.data.message);
      } else {
        setActionErrors((prev) => ({ ...prev, [id]: response.data.message }));
      }
    } catch (err) {
      setActionErrors((prev) => ({ ...prev, [id]: err.message || 'An error occurred while deleting the user profile.' }));
    }
  };

  const refreshCache = async () => {
    clearMessages();
    try {
      const response = await waApi.get(process.env.REACT_APP_REFRESH_WA_USER_PROFILE_CACHE_ENDPOINT);
      if (response.data.httpStatus === 200) {
        setCreateMessage(response.data.message);
      } else {
        setCreateError(response.data.message);
      }
    } catch (err) {
      setCreateError(err.message || 'An error occurred while refreshing the cache.');
    }
  };

  const applyFilters = () => {
    clearMessages();
    setLoading(true);
    if (page !== 0) {
      setPage(0); // triggers the fetch through the page dependency
    } else {
      fetchProfiles(filters);
    }
  };

  const handleNextPage = () => {
    clearMessages();
    if (page + 1 < totalPages) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    clearMessages();
    if (page > 0) {
      setPage((prevPage) => prevPage - 1);
    }
  };

  const handleSizeChange = (e) => {
    clearMessages();
    setSize(Number(e.target.value));
    setPage(0);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading user profiles: {error}</div>;

  return (
    <div className="page-container">
      <h1>User Profiles</h1>

      <div className="flex-row filter-section">
        <div className="filter-item">
          <label htmlFor="items-per-page">Items per page: </label>
          <select id="items-per-page" onChange={handleSizeChange} value={size} className="w-auto">
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <h3>Filters</h3>
      <div className="flex-row filter-section">
        <div className="filter-item">
          <label>Id:</label>
          <input type="text" name="id" value={filters.id ?? ''} onChange={handleFilterInputChange} placeholder="Only numeric value" />
        </div>
        <div className="filter-item">
          <label>User Id:</label>
          <input type="text" name="userId" value={filters.userId ?? ''} onChange={handleFilterInputChange} />
        </div>
        <div className="filter-item">
          <label>User Role:</label>
          <select name="userRole" value={filters.userRole ?? ''} onChange={handleFilterInputChange}>
            <option value="">All</option>
            {USER_ROLES.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        </div>
        <div className="filter-item">
          <label>Email:</label>
          <input type="text" name="email" value={filters.email ?? ''} onChange={handleFilterInputChange} />
        </div>
        <div className="filter-item">
          <label>First Name:</label>
          <input type="text" name="firstName" value={filters.firstName ?? ''} onChange={handleFilterInputChange} />
        </div>
        <div className="filter-item">
          <label>Org Id:</label>
          <input type="text" name="orgId" value={filters.orgId ?? ''} onChange={handleFilterInputChange} />
        </div>
        <div className="filter-item">
          <label>Emp Id:</label>
          <input type="text" name="empId" value={filters.empId ?? ''} onChange={handleFilterInputChange} />
        </div>
        <div className="filter-item">
          <label>Status:</label>
          <select name="status" onChange={handleStatusFilterChange}
                  value={filters.status === null ? '' : filters.status ? 'Active' : 'Inactive'}>
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
        <div className="filter-item">
          <label>WABA:</label>
          <select name="wabaId" value={filters.wabaId ?? ''} onChange={handleFilterInputChange}>
            <option value="">All</option>
            {wabaOptions.map((waba) => (
              <option key={waba.wabaId} value={waba.wabaId}>{waba.wabaId}</option>
            ))}
          </select>
        </div>

        <button type="button" onClick={applyFilters}>Apply Filters</button>
        <button type="button" onClick={resetFilters}>Reset Filters</button>
      </div>

      {canManage && (
        <div className="action-bar">
          <button onClick={openCreateModal}>+ Create New</button>
          <button type="button" onClick={refreshCache}>Refresh Cache</button>
        </div>
      )}

      {createMessage && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createMessage}
        </div>
      )}

      {createError && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
          {createError}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { clearMessages(); setModalOpen(false); setEditingId(null); }}>
        <div className="page-container">
          {formError && Object.values(formError).some(val => val !== null && val !== undefined && val !== "") && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
              <strong>Please fix the following errors:</strong>
              <ul style={{ margin: "8px 0 0 20px" }}>
                {Object.entries(formError)
                  .filter(([_, val]) => val !== null && val !== undefined && val !== "")
                  .map(([key, err], index) => (
                    <li key={index}>{typeof err === "object" ? JSON.stringify(err) : String(err)}</li>
                  ))}
              </ul>
            </div>
          )}
          <form onSubmit={handleSubmit} className="standard-form">
            <h3>{editingId ? 'Edit User Profile' : 'Create New User Profile'}</h3>

            <div className="form-group">
              <label>User Id:</label>
              {/* The unique business key; changing it on an existing row would repoint the record. */}
              <input type="text" name="userId" value={profileForm.userId} onChange={handleInputChange} disabled={!!editingId} required />
            </div>
            <div className="form-group">
              <label>Tenant Id:</label>
              <input type="text" name="tenantId" value={profileForm.tenantId} onChange={handleInputChange} placeholder="Defaults to the signed-in tenant" />
            </div>
            <div className="form-group">
              <label>User Role:</label>
              {/* Constrained to the three values the service and the DB check constraint accept. */}
              <select name="userRole" value={profileForm.userRole} onChange={handleInputChange} required>
                <option value="">-- Select a role --</option>
                {selectableRoles.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input type="email" name="email" value={profileForm.email} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>First Name:</label>
              <input type="text" name="firstName" value={profileForm.firstName} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Last Name:</label>
              <input type="text" name="lastName" value={profileForm.lastName} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Address:</label>
              <input type="text" name="address" value={profileForm.address} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Org Id:</label>
              <input type="text" name="orgId" value={profileForm.orgId} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Emp Id:</label>
              <input type="text" name="empId" value={profileForm.empId} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Activation Status:</label>
              <input type="checkbox" name="status" checked={profileForm.status} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>WABA Accounts (optional, hold Ctrl/Cmd to pick several):</label>
              {selectableWabas.length === 0 ? (
                <div className="profile-empty">
                  No WABA account is linked to your own profile, so there is none to assign.
                </div>
              ) : (
                <select multiple name="wabaIds" value={profileForm.wabaIds} onChange={handleWabaSelectionChange} size={5}>
                  {selectableWabas.map((waba) => (
                    <option key={waba.wabaId} value={waba.wabaId}>{waba.wabaId}</option>
                  ))}
                </select>
              )}
            </div>

            <button type="submit">{editingId ? 'Update Profile' : 'Create Profile'}</button>
          </form>
        </div>
      </Modal>

      <h3>Existing User Profiles</h3>
      {profiles.length === 0 ? (
        <p>No user profiles available</p>
      ) : (
        <div className="table-responsive">
          <table className="notifications-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User Id</th>
                <th>Tenant Id</th>
                <th>Role</th>
                <th>Email</th>
                <th>Name</th>
                <th>Org Id</th>
                <th>Emp Id</th>
                <th>WABA Accounts</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Last Updated At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles?.map((profile) => (
                <tr key={profile.id}>
                  <td>{profile.id}</td>
                  <td>{profile.userId}</td>
                  <td>{profile.tenantId}</td>
                  <td>{profile.userRole}</td>
                  <td>{profile.email}</td>
                  <td>{[profile.firstName, profile.lastName].filter(Boolean).join(' ')}</td>
                  <td>{profile.orgId}</td>
                  <td>{profile.empId}</td>
                  <td>{(profile.wabaIds ?? []).join(', ')}</td>
                  <td>{profile.status ? 'Active' : 'Inactive'}</td>
                  <td>{profile.createdTimestamp ? new Date(profile.createdTimestamp).toLocaleString() : ''}</td>
                  <td>{profile.updatedTimestamp ? new Date(profile.updatedTimestamp).toLocaleString() : ''}</td>
                  <td>
                    {/* A row is only actionable if its role is one the signed-in user could
                        assign: an ADMIN may act on USER rows, a SYSTEM_ADMIN on ADMIN and USER. */}
                    {canManage && assignableRoles.includes(profile.userRole) ? (
                      <div className="action-buttons">
                        <button onClick={() => openEditModal(profile)}>Edit</button>
                        <button onClick={() => toggleActivationStatus(profile.id, profile.status)}>
                          {profile.status ? 'Disable' : 'Enable'}
                        </button>
                        <button onClick={() => handleDelete(profile.id)}>Delete</button>
                      </div>
                    ) : (
                      <span className="no-actions">View only</span>
                    )}
                    {actionErrors[profile.id] && (
                      <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "6px 10px", borderRadius: "6px", marginTop: "6px", fontSize: "12px" }}>
                        {actionErrors[profile.id]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination-container">
        <button onClick={handlePreviousPage} disabled={page === 0}>Previous</button>
        <span>Page {page + 1} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page + 1 >= totalPages}>Next</button>
      </div>
    </div>
  );
};

export default WaUserProfilePage;
